/**
 * Push notification subscription manager
 * Handles service worker registration, permission prompts, and subscription lifecycle
 */

const API_URL = process.env.REACT_APP_BACKEND_URL;

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  return Uint8Array.from([...rawData].map((char) => char.charCodeAt(0)));
}

export async function registerServiceWorker() {
  if (!('serviceWorker' in navigator)) {
    console.warn('Service workers not supported');
    return null;
  }
  try {
    const reg = await navigator.serviceWorker.register('/sw.js');
    return reg;
  } catch (err) {
    console.error('SW registration failed:', err);
    return null;
  }
}

export function isPushSupported() {
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
}

export function getPushPermission() {
  if (!('Notification' in window)) return 'unsupported';
  return Notification.permission; // 'default' | 'granted' | 'denied'
}

export async function subscribeToPush(token) {
  if (!isPushSupported()) {
    return { ok: false, error: 'Push notifications are not supported in this browser' };
  }

  // Request permission
  const permission = await Notification.requestPermission();
  if (permission !== 'granted') {
    return { ok: false, error: permission === 'denied' ? 'Notifications blocked. Please enable them in browser settings.' : 'Permission not granted' };
  }

  try {
    // Get VAPID public key from backend
    const keyResp = await fetch(`${API_URL}/api/channel/push/vapid-key`);
    if (!keyResp.ok) throw new Error('Failed to fetch VAPID key');
    const { public_key } = await keyResp.json();

    // Get or register service worker
    let reg = await navigator.serviceWorker.getRegistration('/sw.js');
    if (!reg) {
      reg = await navigator.serviceWorker.register('/sw.js');
      await navigator.serviceWorker.ready;
    }

    // Subscribe to push
    const subscription = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(public_key),
    });

    const subJson = subscription.toJSON();

    // Send subscription to backend
    const resp = await fetch(`${API_URL}/api/channel/push/subscribe`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        endpoint: subJson.endpoint,
        keys: subJson.keys,
      }),
    });

    if (!resp.ok) {
      const err = await resp.json().catch(() => ({}));
      throw new Error(err.detail || 'Backend subscription failed');
    }

    return { ok: true };
  } catch (err) {
    console.error('Push subscribe error:', err);
    return { ok: false, error: err.message };
  }
}

export async function unsubscribeFromPush(token) {
  try {
    // Unsubscribe from browser
    const reg = await navigator.serviceWorker.getRegistration('/sw.js');
    if (reg) {
      const sub = await reg.pushManager.getSubscription();
      if (sub) await sub.unsubscribe();
    }

    // Tell backend
    await fetch(`${API_URL}/api/channel/push/unsubscribe`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });

    return { ok: true };
  } catch (err) {
    console.error('Push unsubscribe error:', err);
    return { ok: false, error: err.message };
  }
}

export async function getCurrentPushSubscription() {
  try {
    const reg = await navigator.serviceWorker.getRegistration('/sw.js');
    if (!reg) return null;
    return await reg.pushManager.getSubscription();
  } catch {
    return null;
  }
}

export async function sendTestPush(token) {
  const resp = await fetch(`${API_URL}/api/channel/push/test`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  });
  if (!resp.ok) {
    const err = await resp.json().catch(() => ({}));
    throw new Error(err.detail || 'Test push failed');
  }
  return resp.json();
}
